import {
  MultiSellerSpecificCondition,
  PartsRequest,
  SellerStatusResponse,
} from '../api/part/interface'
import { StoreInstances } from '../store/StoreInstancesContainer'
import {
  AstSearchResultsRequest,
  GFXPartsBinRequest,
  ISearchResultsResponse,
  InterchangePartsBinRequest,
  InterchangeRequest,
  InterchangeRequestData,
  InterchangeSearchResultsResponse,
  LinesManufacturer,
  ManufacturerRequestData,
  PartDetails,
  PartDetailsParams,
  PartDetailsRequest,
  PartVehiclesResponse,
  PartsBinRequest,
  SearchRequestBase,
  SearchResultsRequest,
  SearchResultsResponse,
} from '../store/models/SearchModels'
import { UserAttributeKey } from '../store/user/interfaces'
import { BaseServiceProvider } from './BaseServiceProvider'

import { AxiosRequestConfig, AxiosResponse } from 'axios'
import { defaultCredentials } from '../aesInit'
import { embeddedConfigManager } from '../api/embedded/EmbeddedConfigManager'
import { EmbeddedCommunicationsManager } from '../api/embedded/EmbeddedCommunicationsManager'
import { CatalogIndexTracker } from '../api/metrics/CatalogIndexTracker'
import authManager from '../api/security/Auth'
import { IS_INTEGRATED_VERSION } from '../config/ConfigManager'
import { getServiceName } from '../helpers/getServiceName'
import {
  PartAvailabilityRequest,
  PartAvailabilityResponse,
  PartInfoResponse,
} from '../store/cart/ValidationInterfaces'
import { ManufacturerList, PartsList } from '../store/models/SearchModels'
import { getSavedVehicleRegion } from 'libs/cart/src/helpers/vehicleDataStorage'

class PartServiceProvider extends BaseServiceProvider {
  constructor() {
    super(import.meta.env.VITE_API_BASE_URL, 'part', '1.2')
  }

  byPartsApi = async (
    reqBody: PartsRequest,
    excludeAlternates = false,
    coverageKey = '',
    defaultCatalogIndex = false
  ): Promise<SearchResultsResponse> => {
    const resp = await this.post<SearchResultsResponse>(
      'part/byParts',
      reqBody,
      {
        params: {
          catalogId: 99,
          excludeAlternates,
          multiseller: Boolean(coverageKey),
          coverageKey,
        },
        headers: {
          'Catalog-Index': defaultCatalogIndex ? 0 : CatalogIndexTracker.index,
        },
      }
    )
    return resp.data
  }

  getPartDomain = () =>
    StoreInstances?.userStore?.getUserAttribute(UserAttributeKey.partDomain)

  getGeneralSearchResults = async (
    endpointFullUrl: string,
    reqData: SearchResultsRequest
  ): Promise<SearchResultsResponse> => {
    const resp = await this.post<SearchResultsResponse>(
      endpointFullUrl,
      reqData,
      {
        params: {
          partDomain: this.getPartDomain(),
          ...(getSavedVehicleRegion() && { regionId: getSavedVehicleRegion() }),
        },
      }
    )
    return resp.data
  }

  public beforeEachRequest(config: AxiosRequestConfig): AxiosRequestConfig {
    return {
      ...config,
      params: {
        catalogId:
          StoreInstances.searchStore?.currentVehicle?.type?.id === undefined
            ? 111
            : StoreInstances.searchStore.currentVehicle.type.id,
        covListId: StoreInstances.userStore.preferences?.mclOrgId,
        countryId: StoreInstances.userStore.country?.countryCode,
        buyerId:
          IS_INTEGRATED_VERSION &&
          EmbeddedCommunicationsManager.integrationInfo.allowAnyBuyer
            ? embeddedConfigManager.getPartsBasketCreds().buyerId
            : undefined,
        ...config.params,
      },
      headers: {
        'Service-Name': getServiceName(),
        'Catalog-Index': CatalogIndexTracker.index,
        'Partner-Name': IS_INTEGRATED_VERSION
          ? embeddedConfigManager.getPartsBasketCreds().partnerId
          : defaultCredentials.partnerId,
        ...config.headers,
      },
    }
  }

  getSearchResults = async (
    reqData: SearchResultsRequest
  ): Promise<SearchResultsResponse> => {
    const endpointFullUrl = `part/byNavigation`
    const resp = await this.getGeneralSearchResults(endpointFullUrl, reqData)
    return resp
  }

  getDiagramsSearch = async (
    partsBinId: string
  ): Promise<SearchResultsResponse> => {
    const endpointFullUrl = `bin/${partsBinId}/diagramparts`
    const resp = await this.get<SearchResultsResponse>(endpointFullUrl, {
      params: {
        partDomain: this.getPartDomain(),
      },
    })
    return resp.data
  }

  getAstPunchOutSearchResults = async (
    reqData: AstSearchResultsRequest
  ): Promise<SearchResultsResponse> => {
    const endpointFullUrl = `part/byASTNavigation`

    const resp = await this.post<SearchResultsResponse>(
      endpointFullUrl,
      reqData,
      {
        params: {
          catalogId: 7,
        },
        headers: {
          Authorization: await authManager.getCurrentToken(),
        },
      }
    )
    return resp.data
  }

  getSearchResultsByText = async (
    reqData: SearchRequestBase,
    searchText: string,
    queryOverrides?: Record<string, string>
  ): Promise<SearchResultsResponse> => {
    const searchBy = encodeURIComponent(searchText)
    const resp = await this.post<SearchResultsResponse>(
      `part/bySearchTxt/${searchBy}`,
      reqData,
      {
        params: {
          ...queryOverrides,
          ...(getSavedVehicleRegion() && { regionId: getSavedVehicleRegion() }),
        },
      }
    )
    return resp.data
  }

  getAvailability = async (
    partsInfo: PartAvailabilityRequest,
    coverageKey?: string
  ): Promise<{ parts: PartInfoResponse[]; snwPrimaryStatus?: string }> => {
    const partAvailabilityRequest: PartAvailabilityRequest = {
      ...partsInfo,
      clientUTCOffset: StoreInstances.userStore.utcOffset,
    }
    const res = await this.post<PartAvailabilityResponse>(
      'part/availability',
      partAvailabilityRequest,
      {
        params: {
          catalogId: 99,
          multiseller: Boolean(coverageKey),
          coverageKey,
        },
        headers: {
          'Catalog-Index': 0,
        },
      }
    )
    const parts = res.data.parts || []
    const mappedParts = parts.map((part) => {
      /* MPV3-2600 - The endpoint is sending the wrong data type for the locationId field on locations that are valid, furthermore it is sending 
    non valid locations with the correct locationId data type.
    Thus leading to confusion and errors */
      const locations = part.locations
        .filter((l) => typeof l.locationId === 'number') // Collect only the valid locations which have the wrong data type (number)
        .map((l) => {
          return { ...l, locationId: l.locationId?.toString() }
        }) // Map the correct type to the wrong type

      return {
        ...part,
        locations,
      }
    })
    return { parts: mappedParts, snwPrimaryStatus: res.data.snwPrimaryStatus }
  }

  getPartsBinResults = async (
    partsBinId: string,
    reqData: PartsBinRequest,
    queryOverrides?: Record<string, string>
  ): Promise<SearchResultsResponse> => {
    const resp = await this.post<SearchResultsResponse>(
      `bin/${partsBinId}/part`,
      reqData,
      {
        params: {
          ...queryOverrides,
          ...(getSavedVehicleRegion() && { regionId: getSavedVehicleRegion() }),
        },
      }
    )
    return resp.data
  }

  getGfxPartsBinResults = async (
    partsBinId: string,
    reqData: GFXPartsBinRequest
  ): Promise<SearchResultsResponse> => {
    const resp = await this.post<SearchResultsResponse>(
      `bin/${partsBinId}/gfxpart`,
      reqData
    )
    return resp.data
  }

  getInterchangeResults = async (
    reqData: InterchangeRequest
  ): Promise<SearchResultsResponse> => {
    try {
      const params: Record<string, boolean> = {}

      if (IS_INTEGRATED_VERSION) {
        params.epeFlag = !EmbeddedCommunicationsManager.isAstPunchOut
      }
      const resp = await this.post<InterchangeSearchResultsResponse>(
        `part/interchange/parts`,
        reqData,
        {
          params: {
            ...(getSavedVehicleRegion() && {
              regionId: getSavedVehicleRegion(),
            }),
          },
        }
      )

      const mappedResp = { ...resp.data, parts: [] } // Temporary line MPV3-5810
      return mappedResp
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (
        error.status === 400 &&
        error.response.data.code === 'PARTSDISPLAY_1003'
      ) {
        return {
          parts: [],
          invalidPart: true,
        }
      }
    }
    return {
      parts: [],
    }
  }

  getPriceForInterchangeParts = async (
    partsBinId: string,
    reqData: InterchangePartsBinRequest
  ): Promise<SearchResultsResponse> => {
    const resp = await this.post<InterchangeSearchResultsResponse>(
      `part/interchange/${partsBinId}/priceAndAvailability`,
      reqData
    )
    const parts = resp?.data?.partsResult?.flatMap((g) =>
      g.parts.map((p) => ({
        ...p,
        heavyDutyGroup: g.group,
        partType: g.group.partType,
        isLoadingPrices: !resp.data?.isSnwFinished,
      }))
    )

    const mappedResp = { ...resp.data, parts }
    return mappedResp
  }

  getPartVehicles = async (
    partsBinId: string,
    lineCode: number
  ): Promise<PartVehiclesResponse> => {
    const resp = await this.get<PartVehiclesResponse>(
      `bin/${partsBinId}/${lineCode}/vehicles`,
      {
        params: {
          ...(getSavedVehicleRegion() && { regionId: getSavedVehicleRegion() }),
        },
      }
    )
    return resp.data
  }

  clearPartsBinCache = (partsBinId: string): Promise<void> => {
    return this.delete(`bin/${partsBinId}`)
  }

  getLinesManufacturers = async (
    partsBinId: string
  ): Promise<LinesManufacturer[]> => {
    const resp = await this.post<LinesManufacturer[]>(
      `part/${partsBinId}/manufacturers`,
      {},
      {
        params: {
          ...(getSavedVehicleRegion() && { regionId: getSavedVehicleRegion() }),
        },
      }
    )
    return resp.data
  }

  getPartDetails = async (
    reqData: PartDetailsRequest,
    params: PartDetailsParams,
    coverageKey = ''
  ): Promise<PartDetails> => {
    const resp = await this.post<PartDetails>(`part/detail`, reqData, {
      params: {
        allianceProductId: params.allianceProductId,
        lineCode: params.lineCode,
        partNumber: params.partNumber,
        orderNumber: params.orderNumber,
        multiseller: Boolean(coverageKey),
        coverageKey,
        manufacturerID: params.manufacturerID,
        ...(getSavedVehicleRegion() && { regionId: getSavedVehicleRegion() }),
      },
    })
    return resp.data
  }

  getInterchangePartTypes = async (
    InterChangeString: string
  ): Promise<AxiosResponse<PartsList>> => {
    const resp = await this.post<PartsList>(`part/interchange/partTypes`, {
      interchangeText: InterChangeString,
    })
    return resp
  }

  getInterchangeManufacturerData = async (
    data: ManufacturerRequestData
  ): Promise<AxiosResponse<ManufacturerList>> => {
    const resp = await this.post<ManufacturerList>(
      `/part/interchange/manufacturers`,
      data,
      {
        params: {
          ...(getSavedVehicleRegion() && { regionId: getSavedVehicleRegion() }),
        },
      }
    )
    return resp
  }

  getAllInterchangeResults = async (
    reqData: InterchangeRequestData
  ): Promise<ISearchResultsResponse> => {
    const resp = await this.post<ISearchResultsResponse>(
      `part/interchange/parts`,
      reqData,
      {
        params: {
          ...(getSavedVehicleRegion() && { regionId: getSavedVehicleRegion() }),
        },
      }
    )
    return resp.data
  }

  getSellerStatus = async (
    msPartsBinId: string
  ): Promise<SellerStatusResponse> => {
    const resp = await this.get<SellerStatusResponse>(
      `part/${msPartsBinId}/status`,
      { params: [] }
    )
    return resp.data
  }

  updateSpecificConditions = async (
    msPartsBinId: string,
    specificConditions: MultiSellerSpecificCondition[]
  ): Promise<void> => {
    await this.put<void>(
      `part/${msPartsBinId}/specificConditions`,
      specificConditions
    )
  }
}

export default new PartServiceProvider()
